import React, { useState, useRef, useEffect } from 'react';
import { IoMdSend } from "react-icons/io";
import { FaUser, FaChevronDown } from "react-icons/fa";
import { VscRobot } from "react-icons/vsc";
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';

const ChatBox = ({ selectedChat, onNewMessage }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const lastUserScrollPosition = useRef(0);
  const selectedLanguage = selectedChat?.language || 'english';

  useEffect(() => {
    if (selectedChat) {
      setMessages(selectedChat.messages || []);
    } else {
      setMessages([]);
    }
  }, [selectedChat]);

  useEffect(() => {
    const handleScroll = () => {
      if (!chatMessagesRef.current) return;
      
      const { scrollHeight, scrollTop, clientHeight } = chatMessagesRef.current;
      lastUserScrollPosition.current = scrollTop;
      
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    };

    const chatMessages = chatMessagesRef.current;
    if (chatMessages) {
      chatMessages.addEventListener('scroll', handleScroll);
      return () => chatMessages.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    if (messages.length > 0 && chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = lastUserScrollPosition.current;
    }
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      setShowScrollButton(false);
    }
  };

  const getPlaceholderText = () => {
    return selectedLanguage === 'sinhala' 
      ? 'ඔබගේ පණිවිඩය ටයිප් කරන්න...' 
      : 'Type your message...';
  };

  const handleSubmit = async (e, promptText = null) => {
    e.preventDefault();
    const messageToSend = promptText || input.trim();
    if (!messageToSend) return;

    setInput('');
    setLoading(true);

    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const userId = user ? user.id : 'Not available';
      const token = localStorage.getItem('token');

      if (!token) {
        console.error('Token is not available.');
        window.location.href = '/login';
        return;
      }

      if (!userId) {
        console.error('User ID is not available');
        return;
      }

      if (selectedChat) {
        await axios.post(`${process.env.REACT_APP_BACKEND_APP_URL}/api/users/send-message`, {
          chatId: selectedChat._id,
          content: messageToSend,
          role: 'user',
          language: selectedLanguage
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        setMessages(prev => [...prev, { role: 'user', content: messageToSend, language: selectedLanguage }]);

        const modelResponse = await axios.post(`${process.env.REACT_APP_BACKEND_APP_URL}/api/chats/chat`, {
          message: messageToSend,
          chatId: selectedChat._id,
          language: selectedLanguage
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: modelResponse.data.response,
          language: selectedLanguage
        }]);

        onNewMessage();
      } else {
        console.error('No chat selected. Cannot send message.');
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chat-box">
      <div className="chatbox-container">
        <div 
          className="chat-messages" 
          ref={chatMessagesRef}
          style={{ 
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 200px)',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            padding: '1rem'
          }}
        >
          {messages.length === 0 && (
            <div className="empty-chat-message"></div>
          )}
          {messages.map((message, index) => (
            <div
              key={index}
              className={`message ${message.role === 'user' ? 'user' : 'assistant'}`}
            >
              <div className={`message-avatar ${message.role === 'assistant' ? 'assistant-avatar' : ''}`}>
                {message.role === 'user' ? <FaUser /> : <VscRobot />}
              </div>
              <div className={`message-content ${message.role === 'assistant' ? 'assistant-content' : ''}`}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: message.content
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
                      .replace(/\n/g, '<br />')
                  }}
                />
              </div>
            </div>
          ))}
          {loading && <LoadingSpinner />}
          <div ref={messagesEndRef} style={{ height: '1px', marginTop: '10px' }} />
        </div>

        {showScrollButton && (
          <button 
            className="scroll-to-bottom visible" 
            onClick={scrollToBottom}
            aria-label="Scroll to bottom"
          >
            <FaChevronDown />
          </button>
        )}

        <div className="bottom-container">
          <form onSubmit={handleSubmit} className="input-container">
            <div className="input-wrapper">
              <textarea
                className="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={getPlaceholderText()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
              <button 
                type="submit" 
                className="send-button" 
                disabled={loading || !input.trim()}
              >
                <IoMdSend size={20} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatBox;